import { useEffect, useMemo } from 'react';

import { Box, Select, SelectContent, SelectTrigger } from '@eluve/components';
import { useIsUserAppointmentOwner } from '@eluve/frontend-appointment-hooks';
import {
  PromptTemplateSelectItem,
  usePromptTemplates,
} from '@eluve/frontend-feature-user-settings';
import { isLlmOutputDynamicOutput } from '@eluve/graphql-types';
import { useNamedLogger } from '@eluve/logger';
import { useAnalytics } from '@eluve/posthog-react';

interface PromptAndOutputTemplateSelectorProps {
  selectedPromptTemplateId?: string;
  selectedOutputTemplateId?: string;
  onSelectionChanged: (data: {
    initialPromptTemplateId: string;
    initialOutputTemplateId: string | null;
  }) => void;
  usesPortal?: boolean;
  side?: 'bottom' | 'left' | 'right' | 'top';
}

export const PromptAndOutputTemplateSelector: React.FC<
  PromptAndOutputTemplateSelectorProps
> = ({
  selectedPromptTemplateId,
  selectedOutputTemplateId,
  onSelectionChanged,
  usesPortal,
  side,
}) => {
  const {
    activeOutputTemplates: outputTemplates,
    activeSOAPPromptTemplates: promptTemplates,
    defaultPromptTemplateId,
    defaultOutputTemplateId,
    userSettingsOutputTemplateId,
  } = usePromptTemplates();
  const logger = useNamedLogger('TemplateSelector');
  const isUserApptOwner = useIsUserAppointmentOwner();
  const analytics = useAnalytics('prompt-output-template-selector');

  // On load, select the defaults
  useEffect(
    function setDefaultsWhenEmpty() {
      if (!isUserApptOwner) {
        // do not set defaults if user is not appointment owner
        return;
      }

      let areChangesRequired = false;
      const promptTemplate = promptTemplates.find(
        (o) => o.id === selectedPromptTemplateId,
      );
      let initialPromptTemplateId = promptTemplate?.id ?? null;
      const outputTemplate = outputTemplates.find(
        (o) => o.id === selectedOutputTemplateId,
      );
      let initialOutputTemplateId = outputTemplate?.id ?? null;

      if (defaultPromptTemplateId && !initialPromptTemplateId) {
        areChangesRequired = true;
        initialPromptTemplateId = defaultPromptTemplateId;
      }
      const newTemplateOutputType = promptTemplates.find(
        (template) => template.id === initialPromptTemplateId,
      )?.outputType;

      const isDynamicOutput = isLlmOutputDynamicOutput(newTemplateOutputType);

      if (!isDynamicOutput && selectedOutputTemplateId) {
        areChangesRequired = true;
        initialOutputTemplateId = null;
      }
      if (
        isDynamicOutput &&
        !initialOutputTemplateId &&
        defaultOutputTemplateId
      ) {
        areChangesRequired = true;
        initialOutputTemplateId = defaultOutputTemplateId;
      }
      if (areChangesRequired && !initialPromptTemplateId) {
        logger.error('No prompt template selected');
      }

      if (areChangesRequired && initialPromptTemplateId) {
        onSelectionChanged({
          initialPromptTemplateId,
          initialOutputTemplateId,
        });
      }
    },
    [
      logger,
      promptTemplates,
      outputTemplates,
      defaultOutputTemplateId,
      defaultPromptTemplateId,
      selectedPromptTemplateId,
      userSettingsOutputTemplateId,
      selectedOutputTemplateId,
      onSelectionChanged,
      isUserApptOwner,
    ],
  );

  const selectedPromptTemplate = useMemo(
    () =>
      promptTemplates.find(
        (template) => template.id === selectedPromptTemplateId,
      ),
    [selectedPromptTemplateId, promptTemplates],
  );

  const selectedOutputTemplate = useMemo(
    () =>
      outputTemplates.find(
        (template) => template.id === selectedOutputTemplateId,
      ),
    [outputTemplates, selectedOutputTemplateId],
  );

  const selectedTemplateOutputType = selectedPromptTemplate?.outputType ?? null;
  const fetchingTemplates = promptTemplates.length === 0;

  const handlePromptDropdownOpen = (open: boolean) => {
    if (open && promptTemplates) {
      analytics.capture('appointment.template_dropdown_clicked', {
        template_count: promptTemplates.length,
        template_type: 'prompt',
      });
    }
  };

  const handleOutputDropdownOpen = (open: boolean) => {
    if (open && outputTemplates) {
      analytics.capture('appointment.template_dropdown_clicked', {
        template_count: outputTemplates.length,
        template_type: 'output',
      });
    }
  };

  const handlePromptTemplateSelected = (
    promptTemplateId: string,
    isUserAction = true,
  ) => {
    if (isUserAction) {
      analytics.capture('appointment.template_selected', {
        template_id: promptTemplateId,
        template_type: 'prompt',
      });
    }

    const newTemplateOutputType = promptTemplates.find(
      (template) => template.id === promptTemplateId,
    )?.outputType;

    // auto select first output template if DYNAMIC prompt template is selected
    if (
      isLlmOutputDynamicOutput(newTemplateOutputType) &&
      outputTemplates.length > 0
    ) {
      const outputTemplateId =
        userSettingsOutputTemplateId ?? outputTemplates[0]!.id;
      onSelectionChanged({
        initialPromptTemplateId: promptTemplateId,
        initialOutputTemplateId: outputTemplateId,
      });
    } else {
      onSelectionChanged({
        initialPromptTemplateId: promptTemplateId,
        initialOutputTemplateId: null,
      });
    }
  };

  const handleOutputTemplateSelected = (
    outputTemplateId: string,
    isUserAction = true,
  ) => {
    if (isUserAction) {
      analytics.capture('appointment.template_selected', {
        template_id: outputTemplateId,
        template_type: 'output',
      });
    }

    onSelectionChanged({
      initialPromptTemplateId: selectedPromptTemplateId!,
      initialOutputTemplateId: outputTemplateId,
    });
  };

  // If there is only one prompt template, but multiple output templates,
  // the prompt template dropdown is hidden, and the user gets to select from output templates
  const shouldShowPromptTemplates = !(
    promptTemplates?.length === 1 && outputTemplates.length > 1
  );

  return (
    <Box vStack className="w-full">
      {shouldShowPromptTemplates && (
        <Select
          value={selectedPromptTemplateId}
          onValueChange={(promptTemplateId) =>
            handlePromptTemplateSelected(promptTemplateId, true)
          }
          onOpenChange={handlePromptDropdownOpen}
          disabled={!isUserApptOwner}
        >
          <SelectTrigger>
            {fetchingTemplates
              ? 'Loading Templates...'
              : selectedPromptTemplate?.name ?? 'Select Prompt Template'}
          </SelectTrigger>
          <SelectContent side={side} usesPortal={usesPortal}>
            {promptTemplates?.map((template) => (
              <PromptTemplateSelectItem
                key={template.id}
                templateId={template.id ?? ''}
                templateName={template.name ?? ''}
                templateDescription={template.description}
              />
            ))}
          </SelectContent>
        </Select>
      )}

      {isLlmOutputDynamicOutput(selectedTemplateOutputType) &&
        outputTemplates.length > 1 && (
          <Select
            value={selectedOutputTemplateId}
            onValueChange={(outputTemplateId) =>
              handleOutputTemplateSelected(outputTemplateId, true)
            }
            onOpenChange={handleOutputDropdownOpen}
            disabled={!isUserApptOwner}
          >
            <SelectTrigger>
              {(() => {
                if (fetchingTemplates) {
                  return 'Loading Output Templates...';
                }
                return selectedOutputTemplate?.name ?? 'Select Output Template';
              })()}
            </SelectTrigger>
            <SelectContent side={side} usesPortal={usesPortal}>
              {outputTemplates?.map((template) => (
                <PromptTemplateSelectItem
                  key={template.id}
                  templateId={template.id ?? ''}
                  templateName={template.name ?? ''}
                  templateDescription={null}
                />
              ))}
            </SelectContent>
          </Select>
        )}
    </Box>
  );
};
